import React from "react"
import { useTranslation } from "react-i18next"
import i18next from "i18next"

import { fileHash } from "utils/hash"
import { isNotEmpty } from "utils/validation"
import { v4 as uuidv4 } from "uuid"

import { UploadIcon, DownloadIcon } from "components/Elements/Icons"

const VALID_EXTENSIONS = ["jpg", "JPG", "jpeg", "JPEG", "png", "PNG", "pdf", "PDF"]
const VALID_EXTENSIONS_NOPDF = ["jpg", "JPG", "jpeg", "JPEG", "png", "PNG"]

export const InputFile = ({
    value,
    name,
    label = "",
    placeholder = i18next.t("file_placeholder", { ns: "form" }),
    disabled,
    readonly,
    // Styles
    style,
    className = "",
    classNameLabel = "",
    classNameInput = "",
    // Error management
    required = false,
    acceptPDF = true,
    customRules,
    showErrors,
    defaultValue,
    // Handler
    onChange,
    onValidate
}) => {
    const [selectedFile, setSelectedFile] = React.useState(value || null)
    const [fileError, setFileError] = React.useState(false)
    const [errors, setErrors] = React.useState([])
    const { t } = useTranslation()

    const isDisabledOrReadonly = disabled || readonly

    const rules = React.useMemo(() => [
        {
            key: "input-empty",
            check: (value) => required ? isNotEmpty(value) : true,
            message: t("error_mandatory_field", { ns: "form" })
        },
        ...(customRules || [])
    ], [required]) // eslint-disable-line
    // customRules in dependencies array trigger an infinite loop,
    // because custom rules include a check property defining a new function,
    // preventing useMemo to return a valid shallow-comparison
    // or event useDeepMemo to return a valid deep-comparison

    const validate = React.useCallback((currentValue) => {
        const newErrors = []
        rules.forEach(rule => {
            if (!rule.check(currentValue)) {
                newErrors.push({
                    origin: name,
                    rule: rule.key,
                    message: rule.message
                })
            }
        })
        setErrors(newErrors)
        onValidate(name, newErrors)
    }, [rules, name, onValidate])

    // validate on mount and each time value change
    React.useEffect(() => {
        validate(value)
        return () => {
            // On unmounted erros and validation are no longer needed
            setErrors([])
            if (onValidate) onValidate(name, [])
        }
    }, [value, validate, onValidate, name])

    const handleChange = async (event) => {
        const file = [...event.target.files][0]

        if (file) {
            const fileName = file.name
            const fileExtension = fileName.split(".").pop()
            const fileType = file.type === "application/pdf" ? "application/pdf" : "image/png"

            const validExtensions = acceptPDF ? VALID_EXTENSIONS : VALID_EXTENSIONS_NOPDF

            if (validExtensions.indexOf(fileExtension) === -1) {
                setFileError(true)
                return
            }

            const hash_file = await fileHash(file)

            // console.log("hash_pdf", hash_file);

            const reader = new FileReader()
            reader.addEventListener("loadend", (event) => {
                // Update the uploaded file type and header
                let header = ""
                let type = ""

                const arr = (new Uint8Array(event.target.result)).subarray(0, 4)
                for (let i = 0; i < arr.length; i++) {
                    header += arr[i].toString(16)
                }
                // console.log("header",header)

                switch (header) {
                    case "89504e47":
                        type = "image/png"
                        break
                    case "47494638":
                        type = "image/gif"
                        break
                    case "ffd8ffe0":
                    case "ffd8ffe1":
                    case "ffd8ffe2":
                    case "ffd8ffe3":
                    case "ffd8ffe8":
                        type = "image/jpeg"
                        break
                    case "25504446":
                        type = "application/pdf"
                        break
                    default:
                        type = "unknown"
                        break
                }
                // console.log(">>>>>>< type ",type)

                if (!type || type === "unknown") {
                    setFileError(true)
                    return
                }

                // Create the blob file
                const blob = new Blob([new Uint8Array(reader.result)], { type: fileType })

                // Return all values
                const inputValue = {
                    target: {
                        type: "file",
                        name: name,
                        id: uuidv4(),
                        value: hash_file,
                        fileName: fileName,
                        blob: blob,
                        extension: fileExtension
                    }
                  }
                  setFileError(false)
                  setSelectedFile(inputValue.target)
                  onChange(inputValue)
            })
            reader.readAsArrayBuffer(file)
        }
    }

    return (
        <>
            <div className={"c-form-group " + (className)} style={style}>
                {label && (
                    <label className={"c-label mb-1 " + classNameLabel}>{label} {required && "*"}</label>
                )}

                {!isDisabledOrReadonly &&
                    <div>
                        <input
                            type="file"
                            className="u-sr-only"
                            id={name}
                            name={name}
                            onChange={(e) => !readonly && handleChange(e)}
                            onBlur={() => validate(value)}
                            disabled={disabled}
                            readOnly={readonly}
                            required={required}
                        />
                        <label className={`c-input-file "  ${disabled ? "is-disabled" : ""} ${classNameInput}`} htmlFor={name}>
                            <UploadIcon size={20} />
                            <div className="pl-4">
                                {selectedFile
                                    ? <>{selectedFile.fileName}</>
                                    : <>{placeholder}</>
                                }
                            </div>
                        </label>

                        {defaultValue && (
                            <a href={defaultValue.media_url} target="_blank" className={"c-btn c-btn-download mt-5 mb-5"} rel="noreferrer">
                                <DownloadIcon size={20} />
                                <span className="pl-2">{t("global.see")}</span>
                            </a>
                        )}
                    </div>

                }


                {isDisabledOrReadonly && (
                    <>
                        {defaultValue && (
                            <button disabled className={"c-btn c-btn-download mt-5 mb-5"} rel="noreferrer">
                                <DownloadIcon size={20} />
                                <span className="pl-2">{t("global.see")}</span>
                            </button>
                        )}

                        {!defaultValue && (
                            <span className="inline-block italic mt-2">
                                Aucun fichier n'a été chargé
                            </span>
                        )}
                    </>
                )}          

                {fileError &&
                    <p className="text-xs text-danger">{t("file_type", { ns: "form" })} { acceptPDF && t("file_type_pdf", { ns: "form" })}</p>
                }
                {/* Error messages */}
                {showErrors && errors && errors.map((error, index) => {
                    return <p key={index} className="text-xs text-danger">{error.message}</p>
                })}
            </div>
        </>
    )
}